import React, { useState, useEffect, useRef, FunctionComponent, ChangeEvent } from 'react';
import { connect } from 'react-redux';
import CheckboxTree from 'react-checkbox-tree';
import SearchInput from '../../components/SearchInput';
import {
  filterTree,
  mapTree,
  mapFilter
} from './utils';
import { IBindingCallback1 } from '../../common/models/callback';
import { IAppState } from '../../common/models/store/IAppState';
import { ITeam } from '../../common/models/team/ITeam';
import { map, filter as rfilter } from 'ramda';
import { IAssignmentFilter } from '../../common/models/filter/IAssignmentFilter';
import Icon from '../../components/Icon';
import Checkbox from '../../components/Checkbox';
import { AssignmentFilterType } from '../../common/enums/AssignmentFilterType';
import styles from './styles.module.scss';
import { IUserInitials } from '../../common/models/user/IUserInitials';
import { concat } from '../../common/helpers/arrayHelper';
import { IClickedNode } from '../../common/models/common/IClickedNode';
import { User } from '../../common/models/user/User';

interface IAssignmentFilterBodyProps {
  onChange: IBindingCallback1<Partial<IAssignmentFilter>>;
  teams: ITeam[] | null;
  users: IUserInitials[] | null;
  user?: User;
  defaultCheckboxChecked?: string[];
  defaultRadioChecked?: AssignmentFilterType;
  radioChecked?: AssignmentFilterType;
}

const AssignmentFilterBody: FunctionComponent<IAssignmentFilterBodyProps> = ({
  onChange,
  teams,
  users,
  user = {} as User,
  defaultCheckboxChecked = [],
  defaultRadioChecked = AssignmentFilterType.Me,
  radioChecked: radioCheckedProp
}) => {
  const tree = mapTree(teams || [], users || []);
  const userIds = map((u: IUserInitials) => u.id, users || []);

  const [checkedRadio, setCheckedRadio] = useState(
    defaultRadioChecked
  );
  const [checkedNodes, setCheckedNodes] = useState(
    defaultRadioChecked === AssignmentFilterType.Custom
      ? defaultCheckboxChecked
      : []
  );
  const [expandedNodes, setExpandedNodes] = useState<string[]>();
  const [filteredNodes, setFilteredNodes] = useState(tree);
  const [input, setInput] = useState('');

  const requestChange = (radio: AssignmentFilterType, nodes?: string[]) => {
    const assigmentType = radio;
    switch (radio) {
      case AssignmentFilterType.Me: {
        onChange({ userIds: [user.id], teamIds: undefined, assigmentType });
        break;
      }
      case AssignmentFilterType.MyTeam: {
        onChange({ teamIds: [user.teamId], userIds: undefined, assigmentType });
        break;
      }
      case AssignmentFilterType.ShowAll: {
        onChange({ teamIds: [], userIds: [], assigmentType });
        break;
      }
      case AssignmentFilterType.Custom: {
        if ((nodes && nodes.length) || checkedNodes.length) {
          onChange({ ...mapFilter(nodes || checkedNodes, userIds), assigmentType });
        }
        break;
      }
      default:
    }
  };

  const updateCheckedNodes = (ids: string[]) => {
    let newSelected = ids;
    if (newSelected.includes(user.id) && checkedNodes.includes(user.teamId)) {
      newSelected = rfilter(id => id !== user.teamId, ids);
    } else if (newSelected.includes(user.teamId) && checkedNodes.includes(user.id)) {
      newSelected = rfilter(id => id !== user.id, ids);
    }
    setCheckedNodes(newSelected);
    if (newSelected.length && checkedRadio !== AssignmentFilterType.Custom) {
      setCheckedRadio(AssignmentFilterType.Custom);
      return;
    }
    requestChange(AssignmentFilterType.Custom, newSelected);
  };

  const onNodeClick = (node: IClickedNode) => {
    if (!node.children) {
      if (checkedNodes.includes(node.value)) {
        updateCheckedNodes(rfilter(checkedValue => checkedValue !== node.value, checkedNodes));
      } else {
        updateCheckedNodes([...checkedNodes, node.value]);
      }
    }
  };

  useEffect(() => {
    if (!input) {
      setFilteredNodes(tree);
      return;
    }
    setFilteredNodes(filterTree(tree, input));
    setExpandedNodes(['Teams', 'Users']);
  }, [input, teams]);

  useEffect(() => {
    if (expandedNodes) {
      if (checkedRadio !== AssignmentFilterType.Custom && expandedNodes.length > 0) {
        setExpandedNodes([]);
      }
    }
    requestChange(checkedRadio);
  }, [checkedRadio]);

  useEffect(() => {
    if (radioCheckedProp) {
      setCheckedRadio(radioCheckedProp);
    }
  }, [radioCheckedProp]);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus({ preventScroll: true }), []);

  const isCheckedRadio = (name: AssignmentFilterType) => name === checkedRadio;

  const onRadioClicked = (event: ChangeEvent<HTMLInputElement>) => {
    const radio = event.target.name as AssignmentFilterType;
    if (checkedRadio !== radio) {
      setCheckedRadio(radio);
    }
  };

  return (
    <div className="popup-body">
      <div className="popup-search-area">
        <SearchInput
          ref={inputRef}
          value={input}
          onClear={() => setInput('')}
          onChange={setInput}
          intlId="assignmentFilterBody.searchPlaceholder"
        />
      </div>
      <div className={`${styles.radioBlock} d-flex flex-column justify-content-between`}>
        <Checkbox
          name={AssignmentFilterType.Me}
          checked={isCheckedRadio(AssignmentFilterType.Me)}
          radio
          onChange={onRadioClicked}
          label={{ id: 'assignmentFilterBody.me', defaultMessage: `Me (${user.firstName} ${user.lastName})` }}
          additionalText={`(${user.firstName} ${user.lastName})`}
        />
        <Checkbox
          name={AssignmentFilterType.MyTeam}
          checked={isCheckedRadio(AssignmentFilterType.MyTeam)}
          radio
          onChange={onRadioClicked}
          label={{ id: 'assignmentFilterBody.myTeam', defaultMessage: `My team (${user.teamName})` }}
          additionalText={`(${user.teamName})`}
        />
        <Checkbox
          name={AssignmentFilterType.ShowAll}
          checked={isCheckedRadio(AssignmentFilterType.ShowAll)}
          radio
          onChange={onRadioClicked}
          label={{ id: 'assignmentFilterBody.showAll', defaultMessage: 'Show all' }}
        />
        <Checkbox
          name={AssignmentFilterType.Custom}
          checked={isCheckedRadio(AssignmentFilterType.Custom)}
          radio
          onChange={onRadioClicked}
          label={{ id: 'assignmentFilterBody.custom', defaultMessage: 'Custom' }}
        />
      </div>
      <div className={styles.separator} />
      <div className={concat(
        'tree-wrapper',
        checkedRadio !== AssignmentFilterType.Custom && !input.length
          ? 'blind-tree'
          : null
      )}
      >
        <CheckboxTree
          nodes={filteredNodes}
          checked={checkedNodes}
          expanded={expandedNodes}
          onCheck={updateCheckedNodes}
          onExpand={setExpandedNodes}
          onClick={onNodeClick}
          expandOnClick
          icons={{
            check: <Icon icon="checkboxChecked" className="checkbox" />,
            uncheck: <Icon icon="checkboxUnchecked" className="checkbox uncheck" />,
            halfCheck: <Icon icon="checkboxIndeterminate" className="checkbox indeterminate" />,
            expandClose: <Icon icon="chevronUp" />,
            expandOpen: <Icon icon="chevronDown" />,
            parentClose: '',
            leaf: '',
            parentOpen: ''
          }}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({
  teamList: { teams, users }, user: { user }
}: IAppState) => ({ teams, user, users });

export default connect(mapStateToProps, null, null, { forwardRef: true })(AssignmentFilterBody);
